import {
  _isServerEnv,
  getUUID,
  type PrecomputedEvaluationsInterface,
  StatsigSession,
  type StatsigPlugin,
} from "@statsig/client-core";
import { SessionReplay } from "@statsig/session-replay";

interface SessionReplayOptions {
  forceRecording?: boolean;
}

/**
 * Statsig files one replay per Statsig session, and a busy Charm tab keeps its
 * session alive for the full 4 h cap, which makes replays too large to load.
 * Each forced recording window therefore opens its own session, so a replay
 * spans one alert or call. A session that expires mid-window opens the next
 * one instead of ending the recording.
 */
class WindowedReplay extends SessionReplay {
  private _inWindow = false;

  constructor(
    client: PrecomputedEvaluationsInterface,
    options?: SessionReplayOptions,
  ) {
    super(client, options);
    client.on("session_expired", () => {
      queueMicrotask(() => this._reopenIfInWindow());
    });
  }

  private _reopenIfInWindow(): void {
    if (this._inWindow) {
      this._openSession();
    }
  }

  override forceStartRecording(): void {
    this._openSession();
    this._inWindow = true;
  }

  override stopRecording(): void {
    this._inWindow = false;
    super.stopRecording();
  }

  private _openSession(): void {
    this._shutdown("session_expired");
    const { sdkKey, session } = this._client.getContext();
    clearTimeout(session.idleTimeoutID);
    clearTimeout(session.ageTimeoutID);
    StatsigSession.overrideInitialSessionID(getUUID(), sdkKey);
    this._currentSessionID = this._getSessionIdFromClient();
    this._totalLogs = 0;
    super.forceStartRecording();
  }
}

export function sessionReplayPlugin(
  options?: SessionReplayOptions,
): StatsigPlugin<PrecomputedEvaluationsInterface> {
  return {
    __plugin: "session-replay",
    bind: (client) => {
      if (_isServerEnv()) {
        return;
      }
      void new WindowedReplay(client, options);
    },
  };
}
